// slices/topicCacheSlice.ts
import { ITopicCacheSlice, Topic, topicsCacheSlice } from '@/types'

type SetFn = (fn: (state: any) => any) => void

const initialState = {
  topicsList: [],
  pagination: undefined,
  canLoadNextPage: false,
  lastParams: undefined,
}

const adjustPagination = (pagination: any, delta: number) => {
  if (!pagination) return pagination
  const nextCount = (pagination.topicsCount ?? 0) + delta
  return { ...pagination, topicsCount: Math.max(nextCount, 0) }
}

export function createTopicCacheSlice<Name extends string>(sliceName: Name) {
  return (set: SetFn): topicsCacheSlice<Name> => {
    const slice: ITopicCacheSlice = {
      ...initialState,

      reset: () =>
        set((state: any) => ({
          [sliceName]: {
            ...state[sliceName],
            ...initialState,
          },
        })),

      setCacheEntry: (entry: { topicsList: Topic[]; pagination: any; canLoadNextPage: boolean }) =>
        set((state: any) => ({
          [sliceName]: { ...state[sliceName], ...entry },
        })),

      addTopicToCache: (topic: Topic, position: 'start' | 'end' = 'start') =>
        set((state: any) => {
          const existingList = state[sliceName].topicsList
          const topicsList =
            position === 'start' ? [topic, ...existingList] : [...existingList, topic]
          return {
            [sliceName]: {
              ...state[sliceName],
              topicsList,
              pagination: adjustPagination(state[sliceName].pagination, 1),
            },
          }
        }),

      updateTopicInCache: (topicID: string, updater: (topic: Topic) => Topic) =>
        set((state: any) => ({
          [sliceName]: {
            ...state[sliceName],
            topicsList: state[sliceName].topicsList.map((t: Topic) =>
              t.topicID === topicID ? updater(t) : t,
            ),
          },
        })),

      addOrUpdateTopicInCache: (
        topicID: string,
        updater: (topic: Topic | undefined) => Topic,
        position: 'start' | 'end' = 'start',
      ) =>
        set((state: any) => {
          const list: Topic[] = state[sliceName].topicsList
          const idx = list.findIndex((t: Topic) => t.topicID === topicID)

          if (idx === -1) {
            const newTopic = updater(undefined)
            const topicsList = position === 'end' ? [...list, newTopic] : [newTopic, ...list]
            return {
              [sliceName]: {
                ...state[sliceName],
                topicsList,
                pagination: adjustPagination(state[sliceName].pagination, 1),
              },
            }
          }

          const topicsList = [...list]
          topicsList[idx] = updater(topicsList[idx])
          return { [sliceName]: { ...state[sliceName], topicsList } }
        }),

      removeTopicFromCache: (topicID: string) =>
        set((state: any) => {
          const list: Topic[] = state[sliceName].topicsList
          const existed = list.some((t: Topic) => t.topicID === topicID)
          const topicsList = list.filter((t: Topic) => t.topicID !== topicID)

          return {
            [sliceName]: {
              ...state[sliceName],
              topicsList,
              pagination: existed
                ? adjustPagination(state[sliceName].pagination, -1)
                : state[sliceName].pagination,
            },
          }
        }),
    }

    return { [sliceName]: slice } as topicsCacheSlice<Name>
  }
}
